// React Query hook for ignored issues state management
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../services/api';
import { useIgnoredIssuesService, type IgnoredIssuesData, type IgnoredIssuesCount } from '../../../services/ignoredIssues.service';

// Query keys
export const ignoredIssuesKeys = {
    all: ['ignoredIssues'] as const,
    lists: () => [...ignoredIssuesKeys.all, 'list'] as const,
    list: (taxYear?: string | number) => [...ignoredIssuesKeys.lists(), taxYear] as const,
    counts: () => [...ignoredIssuesKeys.all, 'count'] as const,
    count: (taxYear?: string | number) => [...ignoredIssuesKeys.counts(), taxYear] as const,
    detail: (issueId: string | number, taxYear: string | number) => 
        [...ignoredIssuesKeys.all, 'detail', issueId, taxYear] as const,
};

/**
 * Hook to fetch ignored issues for a specific tax year
 */
export const useIgnoredIssues = (taxYear?: string | number) => {
    const apiClient = useApiClient();
    const ignoredIssuesService = useIgnoredIssuesService(apiClient);

    return useQuery({
        queryKey: ignoredIssuesKeys.list(),
        queryFn: () => ignoredIssuesService!.getIgnoredIssues(taxYear),
        enabled: !!ignoredIssuesService,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch ignored issues count for a specific tax year
 */
export const useIgnoredIssuesCount = (taxYear?: string | number) => {
    const apiClient = useApiClient();
    const ignoredIssuesService = useIgnoredIssuesService(apiClient);

    return useQuery({
        queryKey: ignoredIssuesKeys.count(),
        queryFn: () => ignoredIssuesService!.getIgnoredIssuesCount(taxYear),
        enabled: !!ignoredIssuesService,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to check if a specific issue is ignored
 */
export const useIsIssueIgnored = (issueId?: string | number, taxYear?: string | number) => {
    const apiClient = useApiClient();
    const ignoredIssuesService = useIgnoredIssuesService(apiClient);

    return useQuery({
        queryKey: ignoredIssuesKeys.detail(issueId!, taxYear!),
        queryFn: () => ignoredIssuesService!.isIssueIgnored(issueId!, taxYear!),
        enabled: !!ignoredIssuesService && !!issueId && !!taxYear,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to ignore an issue with optimistic updates
 */
export const useIgnoreIssueMutation = () => {
    const apiClient = useApiClient();
    const ignoredIssuesService = useIgnoredIssuesService(apiClient);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            issueId,
            taxYear,
            issueType,
            currency,
        }: {
            issueId: string | number;
            taxYear: string | number;
            issueType: 'costBasis' | 'missingAssetValue';
            currency: string;
        }) => {
            if (!ignoredIssuesService) throw new Error('Service not available');
            return ignoredIssuesService.ignoreIssue(issueId, taxYear, issueType, currency);
        },
        onMutate: async ({ issueId, taxYear, issueType, currency }) => {
            // Cancel outgoing refetches (use correct query keys)
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.list() });
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.count() });
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.detail(issueId, taxYear) });

            // Snapshot previous values (use correct query keys)
            const previousIgnoredIssues = queryClient.getQueryData<IgnoredIssuesData>(
                ignoredIssuesKeys.list()
            );
            const previousCount = queryClient.getQueryData<IgnoredIssuesCount>(
                ignoredIssuesKeys.count()
            );

            // Optimistically update ignored issues list (use correct query key)
            queryClient.setQueryData<IgnoredIssuesData>(ignoredIssuesKeys.list(), (old = {}) => ({
                ...old,
                [issueId.toString()]: {
                    id: issueId,
                    type: issueType,
                    currency,
                    timestamp: Date.now(),
                },
            }));

            // Optimistically update count (use correct query key)
            queryClient.setQueryData<IgnoredIssuesCount>(ignoredIssuesKeys.count(), (old) => {
                if (!old) return { costBasis: 0, missingAssetValue: 0, total: 1 };
                const newCount = {
                    ...old,
                    [issueType]: old[issueType] + 1,
                    total: old.total + 1,
                };
                return newCount;
            });

            // Optimistically update individual issue status
            queryClient.setQueryData(ignoredIssuesKeys.detail(issueId, taxYear), true);

            return { previousIgnoredIssues, previousCount };
        },
        onError: (error, { issueId, taxYear }, context) => {
            // Revert optimistic updates (use correct query keys)
            if (context?.previousIgnoredIssues) {
                queryClient.setQueryData(ignoredIssuesKeys.list(), context.previousIgnoredIssues);
            }
            if (context?.previousCount) {
                queryClient.setQueryData(ignoredIssuesKeys.count(), context.previousCount);
            }
            queryClient.setQueryData(ignoredIssuesKeys.detail(issueId, taxYear), false);
        },
        onSettled: (data, error, { issueId, taxYear }) => {
            // Refetch to ensure consistency
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.list() });
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.count() });
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.detail(issueId, taxYear) });
        },
    });
};

/**
 * Hook to unignore an issue with optimistic updates
 */
export const useUnignoreIssueMutation = () => {
    const apiClient = useApiClient();
    const ignoredIssuesService = useIgnoredIssuesService(apiClient);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            issueId,
            taxYear,
        }: {
            issueId: string | number;
            taxYear: string | number;
        }) => {
            if (!ignoredIssuesService) throw new Error('Service not available');
            return ignoredIssuesService.unignoreIssue(issueId, taxYear);
        },
        onMutate: async ({ issueId, taxYear }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.list() });
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.count() });
            await queryClient.cancelQueries({ queryKey: ignoredIssuesKeys.detail(issueId, taxYear) });

            // Snapshot previous values
            const previousIgnoredIssues = queryClient.getQueryData<IgnoredIssuesData>(
                ignoredIssuesKeys.list()
            );
            const previousCount = queryClient.getQueryData<IgnoredIssuesCount>(
                ignoredIssuesKeys.count()
            );

            // Get the issue data before removing it
            const issueData = previousIgnoredIssues?.[issueId.toString()];

            // Optimistically update ignored issues list
            queryClient.setQueryData<IgnoredIssuesData>(ignoredIssuesKeys.list(), (old = {}) => {
                const updated = { ...old };
                delete updated[issueId.toString()];
                return updated;
            });

            // Optimistically update count
            if (issueData) {
                queryClient.setQueryData<IgnoredIssuesCount>(ignoredIssuesKeys.count(), (old) => {
                    if (!old) return { costBasis: 0, missingAssetValue: 0, total: 0 };
                    const newCount = {
                        ...old,
                        [issueData.type]: Math.max(0, old[issueData.type] - 1),
                        total: Math.max(0, old.total - 1),
                    };
                    return newCount;
                });
            }

            // Optimistically update individual issue status
            queryClient.setQueryData(ignoredIssuesKeys.detail(issueId, taxYear), false);

            return { previousIgnoredIssues, previousCount };
        },
        onError: (error, { issueId, taxYear }, context) => {
            // Revert optimistic updates
            if (context?.previousIgnoredIssues) {
                queryClient.setQueryData(ignoredIssuesKeys.list(), context.previousIgnoredIssues);
            }
            if (context?.previousCount) {
                queryClient.setQueryData(ignoredIssuesKeys.count(), context.previousCount);
            }
            queryClient.setQueryData(ignoredIssuesKeys.detail(issueId, taxYear), true);
        },
        onSettled: (data, error, { issueId, taxYear }) => {
            // Refetch to ensure consistency
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.list() });
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.count() });
            queryClient.invalidateQueries({ queryKey: ignoredIssuesKeys.detail(issueId, taxYear) });
        },
    });
};

/**
 * Utility hook to check if an issue is ignored (synchronous based on cached data)
 * This is useful for UI components that need immediate response
 */
export const useIsIssueIgnoredSync = (issueId: string | number, taxYear: string | number): boolean => {
    const queryClient = useQueryClient();
    
    // First try to get from detailed query cache
    const detailResult = queryClient.getQueryData<boolean>(ignoredIssuesKeys.detail(issueId, taxYear));
    if (detailResult !== undefined) {
        return detailResult;
    }
    
    // Fall back to checking the list cache
    const listResult = queryClient.getQueryData<IgnoredIssuesData>(ignoredIssuesKeys.list());
    if (listResult) {
        return issueId.toString() in listResult;
    }
    
    return false;
};